import { postStorefrontCanvasLoaded } from 'components/storefront/editor/utils/post-message';

const ASSET_LOAD_PHASES = Object.freeze({
  BEFORE_CONTENT: 'layout:before-content',
  CONTENT: 'content',
  AFTER_CONTENT: 'layout:after-content',
});

const ASSET_LOAD_ORDER = Object.freeze([
  ASSET_LOAD_PHASES.BEFORE_CONTENT,
  ASSET_LOAD_PHASES.CONTENT,
  ASSET_LOAD_PHASES.AFTER_CONTENT,
]);

class AssetLoader {
  #loaders = new Map();
  #initialSectionPaths = new Set();
  #contentRenderStarted = false;
  #loaded = false;

  constructor(initialSectionPaths) {
    this.#initialSectionPaths = initialSectionPaths;
  }

  /**
   * @param {() => Promise<void>} loader
   * @param {number} priority
   */
  addLayoutAsset(loader, priority) {
    this.#addAsset(loader, 'layout', priority);
  }

  /**
   * @param {() => Promise<void>} loader
   * @param {number} priority
   */
  addContentAsset(loader, priority) {
    this.#addAsset(loader, 'content', priority);
  }

  onContentRenderStart() {
    this.#contentRenderStarted = true;
  }

  onSectionMount(path) {
    this.#initialSectionPaths.delete(path);

    if (this.#areAllSectionsMounted()) {
      this.#load().then(() => {
        if (!this.#loaded) {
          this.#loaded = true;
          postStorefrontCanvasLoaded(window.parent);
        }
      });
    }
  }

  async #load() {
    if (this.#loaders.size <= 0) {
      return;
    }

    for (const phase of ASSET_LOAD_ORDER) {
      const loadPhase = this.#loaders.get(phase);

      if (!loadPhase || loadPhase.size <= 0) {
        continue;
      }

      const priorities = Array.from(loadPhase.keys()).sort();

      for (const priority of priorities) {
        const loaders = loadPhase.get(priority);

        if (!loaders) {
          continue;
        }

        loadPhase.delete(priority);

        await Promise.allSettled(loaders.map((loader) => loader()));
      }

      this.#loaders.delete(phase);
    }
  }

  /**
   * @param {() => Promise<void>} loader
   * @param {string} type
   * @param {number} priority
   */
  #addAsset(loader, type, priority = 0) {
    const phase = this.#getAssetLoadPhase(type);

    if (!this.#loaders.has(phase)) {
      this.#loaders.set(phase, new Map());
    }

    const loadPhase = this.#loaders.get(phase);

    if (!loadPhase.has(priority)) {
      loadPhase.set(priority, []);
    }

    loadPhase.get(priority).push(loader);
  }

  #getAssetLoadPhase(type) {
    if (type !== 'layout') {
      return ASSET_LOAD_PHASES.CONTENT;
    }

    return this.#contentRenderStarted
      ? ASSET_LOAD_PHASES.AFTER_CONTENT
      : ASSET_LOAD_PHASES.BEFORE_CONTENT;
  }

  #areAllSectionsMounted() {
    return this.#initialSectionPaths.size <= 0;
  }
}

export default AssetLoader;
